import { useState, useMemo } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { useAuth, PERMISSIONS } from '@/contexts/AuthContext'
import { useAccount } from '@/contexts/AccountContext'
import type { Permission } from '@/contexts/AuthContext'
import { Select } from '@/components/ui/select'
import { getAccounts } from '@/features/execution-monitoring/queries/executions'
import type { AccountSummary } from '@/features/execution-monitoring/types'
import {
  Activity,
  BarChart3,
  Briefcase,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code2,
  Database,
  DollarSign,
  GitMerge,
  Key,
  Layers,
  LayoutDashboard,
  ListChecks,
  LogOut,
  PackageSearch,
  Search,
  Server,
  Settings,
  ShieldAlert,
  SlidersHorizontal,
  Timer,
  Upload,
  User,
  Users,
  Wallet,
  Workflow,
  Scale,
} from 'lucide-react'

interface NavItem {
  title: string
  href: string
  icon: React.ReactNode
  badge?: string
  disabled?: boolean
  // Required permissions to see this menu item (if empty, visible to all authenticated users)
  permissions?: (Permission | string)[]
  // Sub-pages shown as an expandable submenu under this item. Parent becomes a toggle
  // (not a direct link) once it has children — see rendering logic below.
  children?: NavItem[]
}

// Navigation items with permission requirements
const allNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/',
    icon: <LayoutDashboard className="h-5 w-5" />,
    // Dashboard is visible to anyone with at least one dashboard permission
    permissions: [PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD, PERMISSIONS.FINANCE_MONITORING_DASHBOARD],
  },
  {
    title: 'Execution Monitoring',
    href: '/executions',
    icon: <Activity className="h-5 w-5" />,
    permissions: [PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD],
  },
  {
    title: 'Conversion Reports',
    href: '/attribution',
    icon: <BarChart3 className="h-5 w-5" />,
    permissions: [PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD],
  },
  {
    title: 'Finance',
    href: '/finance',
    icon: <DollarSign className="h-5 w-5" />,
    permissions: [PERMISSIONS.FINANCE_MONITORING_DASHBOARD],
    children: [
      {
        title: 'Finance Dashboard',
        href: '/finance',
        icon: <DollarSign className="h-4 w-4" />,
        permissions: [PERMISSIONS.FINANCE_MONITORING_DASHBOARD],
      },
      {
        title: 'Consumables Report',
        href: '/finance/consumables',
        icon: <BarChart3 className="h-4 w-4" />,
        permissions: [PERMISSIONS.FINANCE_MONITORING_DASHBOARD],
      },
      {
        title: 'Usage Adjustments',
        href: '/finance/adjustments',
        icon: <SlidersHorizontal className="h-4 w-4" />,
        permissions: [PERMISSIONS.FINANCE_MONITORING_DASHBOARD],
      },
      {
        title: 'Consumable Balances',
        href: '/finance/balances',
        icon: <Wallet className="h-4 w-4" />,
        permissions: [PERMISSIONS.FINANCE_MONITORING_DASHBOARD],
      },
    ],
  },
  {
    title: 'Batch Jobs',
    href: '/batch-jobs',
    icon: <Timer className="h-5 w-5" />,
    permissions: [PERMISSIONS.BATCH_JOB_MONITORING],
  },
  {
    title: 'Developer Console',
    href: '/developer-console',
    icon: <Code2 className="h-5 w-5" />,
    permissions: [PERMISSIONS.DEVELOPER_CONSOLE],
  },
  {
    title: 'Derivation Config',
    href: '/derivation-config',
    icon: <Layers className="h-5 w-5" />,
    permissions: [PERMISSIONS.DERIVATION_CONFIG],
  },
  {
    title: 'Data Ingestion',
    href: '/data-ingestion',
    icon: <Upload className="h-5 w-5" />,
    permissions: [PERMISSIONS.DATA_INGESTION],
  },
  {
    title: 'Sync Validation',
    href: '/sync-validation',
    icon: <GitMerge className="h-5 w-5" />,
    permissions: [PERMISSIONS.SYNC_MONITORING],
  },
  {
    title: 'Parity Monitor',
    href: '/parity-monitor',
    icon: <Scale className="h-5 w-5" />,
    permissions: [PERMISSIONS.SYNC_MONITORING],
  },
  {
    title: 'Pipeline Monitor',
    href: '/pipeline-monitoring',
    icon: <Workflow className="h-5 w-5" />,
    permissions: [PERMISSIONS.PIPELINE_MONITORING],
  },
  {
    title: 'Pipeline Health',
    href: '/pipeline-topology',
    icon: <Activity className="h-5 w-5" />,
    permissions: [PERMISSIONS.PIPELINE_MONITORING],
  },
  {
    title: 'Message Trace',
    href: '/message-trace',
    icon: <Search className="h-5 w-5" />,
    permissions: [PERMISSIONS.PIPELINE_MONITORING],
  },
  {
    title: 'Event Trace',
    href: '/event-trace',
    icon: <ListChecks className="h-5 w-5" />,
    permissions: [PERMISSIONS.PIPELINE_MONITORING],
  },
  {
    title: 'Failed Events',
    href: '/non-unifiable-monitoring',
    icon: <ShieldAlert className="h-5 w-5" />,
    permissions: [PERMISSIONS.NON_UNIFIABLE_MONITORING],
  },
  {
    title: 'MongoDB Console',
    href: '/mongodb',
    icon: <Database className="h-5 w-5" />,
    permissions: [PERMISSIONS.MONGO_CONSOLE_VIEW],
  },
  {
    title: 'Redis Console',
    href: '/redis-console',
    icon: <Server className="h-5 w-5" />,
    permissions: [PERMISSIONS.REDIS_CONSOLE],
  },
  {
    title: 'Product Data',
    href: '/product-data',
    icon: <PackageSearch className="h-5 w-5" />,
    permissions: [PERMISSIONS.PRODUCT_DATA],
  },
  {
    title: 'Accounts',
    href: '/accounts',
    icon: <Briefcase className="h-5 w-5" />,
    permissions: [PERMISSIONS.ACCOUNT_MANAGEMENT],
  },
  {
    title: 'API Keys',
    href: '/api-keys',
    icon: <Key className="h-5 w-5" />,
    permissions: [PERMISSIONS.API_KEY_MANAGEMENT],
  },
  {
    title: 'Users',
    href: '/users',
    icon: <Users className="h-5 w-5" />,
    badge: 'Soon',
    disabled: true,
    permissions: [PERMISSIONS.USER_MANAGEMENT],
  },
  {
    title: 'Settings',
    href: '/settings',
    icon: <Settings className="h-5 w-5" />,
    badge: 'Soon',
    disabled: true,
    // Settings visible to all authenticated users
    permissions: [],
  },
]

export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasAnyPermission } = useAuth()
  const { selectedAccountId, setSelectedAccountId } = useAccount()

  // Fetch accounts for the filter
  const { data: accounts = [] } = useQuery<AccountSummary[]>({
    queryKey: ['accounts'],
    queryFn: getAccounts,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Build account options
  const accountOptions = [
    { value: 'ALL', label: 'All Accounts' },
    ...accounts.map(account => ({
      value: account.accId.toString(),
      label: `${account.accountName} (${account.accId})`,
    })),
  ]

  // Filter navigation items (and their submenu children, if any) based on user permissions
  const navItems = useMemo(() => {
    const isVisible = (item: NavItem) => {
      // If no permissions required, item is visible to all authenticated users
      if (!item.permissions || item.permissions.length === 0) return true
      // Check if user has any of the required permissions
      return hasAnyPermission(item.permissions)
    }
    return allNavItems
      .filter(isVisible)
      .map(item =>
        item.children ? { ...item, children: item.children.filter(isVisible) } : item
      )
  }, [hasAnyPermission])

  const toggleExpanded = (href: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(href)) {
        next.delete(href)
      } else {
        next.add(href)
      }
      return next
    })
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          'flex flex-col border-r bg-card transition-all duration-300',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b px-4">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <span className="text-sm font-bold">FH</span>
              </div>
              <span className="font-semibold">Admin Portal</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2">
          {navItems.map((item) => {
            // Items with children (and a non-collapsed sidebar) render as an expandable
            // section instead of a direct link — the section itself has no distinct page,
            // "Finance Dashboard" (the first child) covers that.
            if (item.children && item.children.length > 0 && !collapsed) {
              const sectionActive = location.pathname.startsWith(item.href)
              const isExpanded = expandedItems.has(item.href) || sectionActive
              return (
                <div key={item.href}>
                  <button
                    type="button"
                    onClick={() => toggleExpanded(item.href)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                      sectionActive
                        ? 'bg-accent text-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    )}
                  >
                    {item.icon}
                    <span className="flex-1 text-left">{item.title}</span>
                    <ChevronDown
                      className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
                    />
                  </button>
                  {isExpanded && (
                    <div className="mt-1 space-y-1 pl-4">
                      {item.children.map((child) => {
                        const isChildActive = location.pathname === child.href
                        return (
                          <NavLink
                            key={child.href}
                            to={child.href}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                              isChildActive
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            )}
                          >
                            {child.icon}
                            <span className="flex-1">{child.title}</span>
                          </NavLink>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            const isActive = location.pathname === item.href
            return (
              <NavLink
                key={item.href}
                to={item.disabled ? '#' : item.href}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  item.disabled && 'cursor-not-allowed opacity-50'
                )}
                onClick={(e) => item.disabled && e.preventDefault()}
              >
                {item.icon}
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.title}</span>
                    {item.badge && (
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* User & Logout */}
        <div className="border-t p-2">
          {/* User info */}
          {user && (
            <div
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 mb-1',
                collapsed ? 'justify-center' : ''
              )}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                <User className="h-4 w-4" />
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user.username || user.email || 'User'}
                  </p>
                  {user.accountId && (
                    <p className="text-xs text-muted-foreground">
                      Account: {user.accountId}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Logout button */}
          <button
            onClick={handleLogout}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm w-full transition-colors',
              'text-red-600 hover:bg-red-50 hover:text-red-700',
              collapsed ? 'justify-center' : ''
            )}
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>

        {/* Footer */}
        <div className="border-t p-4">
          {!collapsed && (
            <div className="text-xs text-muted-foreground">
              <p>FirstHive Admin Portal</p>
              <p>v1.0.0</p>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar with Account Filter */}
        <div className="h-16 border-b bg-white px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Account Filter:</span>
          </div>
          <div className="w-80">
            <Select
              options={accountOptions}
              value={selectedAccountId || 'ALL'}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedAccountId(e.target.value)
              }
            />
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
